import { type LocationResult } from '@/components/location-autocomplete';
import { apiClient } from '@/lib/apiClient';
import { useCallback, useEffect, useRef, useState } from 'react';

interface LocationSuggestionsResponse {
    cities: LocationResult[];
    states: LocationResult[];
}

interface UseLocationSuggestionsOptions {
    /** Desligado: não faz requisição e devolve sempre lista vazia. */
    enabled: boolean;
    searchType: 'embarcacoes' | 'empresas';
    minChars?: number;
    debounceMs?: number;
    limit?: number;
}

/**
 * Estados e cidades para um texto livre (/api/locations/autocomplete), no formato do filtro
 * de localização das buscas ("Cidade - UF", cidades com mais itens ativos primeiro).
 */
export function useLocationSuggestions({ enabled, searchType, minChars = 2, debounceMs = 300, limit = 4 }: UseLocationSuggestionsOptions) {
    const [locations, setLocations] = useState<LocationResult[]>([]);
    const [loading, setLoading] = useState(false);
    const debounceTimerRef = useRef<number | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const cancelPending = () => {
        if (debounceTimerRef.current) {
            window.clearTimeout(debounceTimerRef.current);
        }

        abortControllerRef.current?.abort();
    };

    const searchLocations = useCallback(
        (query: string) => {
            if (!enabled) {
                return;
            }

            cancelPending();

            if (query.trim().length < minChars) {
                setLocations([]);
                setLoading(false);
                return;
            }

            setLoading(true);

            debounceTimerRef.current = window.setTimeout(async () => {
                const controller = new AbortController();
                abortControllerRef.current = controller;

                try {
                    const response = await apiClient.get<LocationSuggestionsResponse>('/api/locations/autocomplete', {
                        params: { q: query.trim(), type: searchType },
                        signal: controller.signal,
                    });

                    setLocations([...response.data.states, ...response.data.cities].slice(0, limit));
                    setLoading(false);
                } catch (error: unknown) {
                    if ((error as { name?: string })?.name !== 'CanceledError') {
                        console.error('Location suggestions error:', error);
                        setLocations([]);
                        setLoading(false);
                    }
                }
            }, debounceMs);
        },
        [debounceMs, enabled, limit, minChars, searchType],
    );

    const clearLocations = useCallback(() => {
        cancelPending();
        setLocations([]);
        setLoading(false);
    }, []);

    useEffect(() => cancelPending, []);

    return { locations, loading, searchLocations, clearLocations };
}
